import { useCallback, useEffect, useState } from 'react';
import { RefreshCw, KeyRound, CalendarSearch, X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

function authFetch(url, options = {}) {
  const token = localStorage.getItem('ozel_token');
  return fetch(url, {
    ...options,
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    credentials: 'include',
  });
}
const json = (body) => ({ headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
const tijd = (s) => new Date(s).toLocaleString('nl-NL', { dateStyle: 'medium', timeStyle: 'short' });
const uur = (n) => (Math.round(n * 100) / 100).toLocaleString('nl-NL', { maximumFractionDigits: 2 });

// Koppeling met FlexDamen: status, inloggegevens en welke weken je ophaalt.
export default function DamenKoppeling({ profiel, profielNaam, opgeslagen = [], onKlaar }) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [st, setSt] = useState(null);
  const [fout, setFout] = useState('');
  const [paneel, setPaneel] = useState(null); // 'inlog' | 'weken' | null
  const [inlog, setInlog] = useState({ email: '', wachtwoord: '' });
  const [inlogInfo, setInlogInfo] = useState(null);
  const [gekozen, setGekozen] = useState(new Set());

  const laad = useCallback(() => authFetch(`/api/damen-uren/sync-status?profiel=${profiel}`).then((r) => (r.ok ? r.json() : null)).then((n) => {
    setSt((oud) => {
      if (oud?.bezig && n && !n.bezig) onKlaar?.();
      return n;
    });
  }).catch(() => {}), [onKlaar, profiel]);
  useEffect(() => { laad(); }, [laad]);
  useEffect(() => {
    if (!st?.bezig) return undefined;
    const t = setInterval(laad, 3000);
    return () => clearInterval(t);
  }, [st?.bezig, laad]);

  const start = async (url, body) => {
    setFout('');
    const r = await authFetch(url, { method: 'POST', ...json({ ...(body || {}), profiel }) });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) setFout(d.error || 'Dat lukte niet');
    laad();
    return r.ok;
  };

  const openInlog = async () => {
    setPaneel(paneel === 'inlog' ? null : 'inlog');
    setFout('');
    const r = await authFetch(`/api/damen-uren/instellingen?profiel=${profiel}`);
    const d = r.ok ? await r.json() : {};
    setInlogInfo(d);
    setInlog({ email: d.email || '', wachtwoord: '' });
  };
  const bewaarInlog = async (e) => {
    e.preventDefault();
    setFout('');
    const r = await authFetch('/api/damen-uren/instellingen', { method: 'PUT', ...json({ ...inlog, profiel }) });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) { setFout(d.error || 'Opslaan mislukt'); return; }
    setPaneel(null);
    setInlog({ email: inlog.email, wachtwoord: '' });
    laad();
  };

  const openWeken = () => {
    setPaneel(paneel === 'weken' ? null : 'weken');
    setFout('');
    if (!st?.beschikbaar && !st?.bezig && st?.ingesteld) start('/api/damen-uren/overzicht');
  };
  const wissel = (k) => setGekozen((s) => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const haalGekozen = async () => {
    const weken = [...gekozen].map((k) => { const [jaar, week] = k.split('-').map(Number); return { jaar, week }; });
    if (await start('/api/damen-uren/sync', { weken })) { setGekozen(new Set()); setPaneel(null); }
  };

  const l = st?.laatste;
  const lokaal = Object.fromEntries(opgeslagen.map((w) => [`${w.jaar}-${w.week}`, w]));
  const beschikbaar = st?.beschikbaar?.weken || [];
  const taakTekst = st?.taak === 'overzicht' ? 'Weken opzoeken in FlexDamen…' : 'Uren ophalen uit FlexDamen… (ongeveer een minuut)';

  return (
    <div className="du-kop">
      <div className={`du-sync${l && !l.ok && !st?.bezig ? ' is-fout' : ''}`}>
        <span>
          {st === null ? 'Status laden…'
            : st.bezig && st.bezigProfiel !== profiel ? 'Er loopt nu een taak voor een ander bedrijf; even geduld.'
            : st.bezig ? taakTekst
            : !st.ingesteld ? 'Nog niet gekoppeld: stel eerst de Damen-inloggegevens in.'
            : l && !l.ok ? `Mislukt: ${l.fout}`
            : l?.taak === 'ophalen' ? `Laatst opgehaald ${tijd(l.op)} · ${l.perWeek.map((w) => `week ${w.week}: ${w.aantal}`).join(', ')}`
            : 'Wordt elke ochtend om 06:30 automatisch bijgewerkt (laatste 3 weken).'}
          {fout && <strong> {fout}</strong>}
        </span>
        <div className="du-sync-knoppen">
          {isAdmin && <button type="button" className="du-btn" onClick={openInlog} aria-expanded={paneel === 'inlog'}><KeyRound size={15} /> Inloggegevens</button>}
          <button type="button" className="du-btn" onClick={openWeken} disabled={!st?.ingesteld} aria-expanded={paneel === 'weken'}><CalendarSearch size={15} /> Weken kiezen</button>
          <button type="button" className="du-btn du-btn-primary" onClick={() => start('/api/damen-uren/sync', { weken: 3 })} disabled={!st?.ingesteld || st?.bezig}>
            <RefreshCw size={15} className={st?.bezig ? 'du-spin' : ''} /> {st?.bezig ? 'Bezig…' : 'Laatste 3 weken'}
          </button>
        </div>
      </div>

      {paneel === 'inlog' && (
        <form className="du-paneel" onSubmit={bewaarInlog}>
          <button type="button" className="du-sluit" onClick={() => setPaneel(null)} aria-label="Sluiten"><X size={16} /></button>
          <h4>Inloggegevens FlexDamen · {profielNaam}</h4>
          <p>Gebruik het algemene kantooraccount van {profielNaam}. Het wachtwoord wordt versleuteld opgeslagen en daarna nooit meer getoond.
            {inlogInfo?.bijgewerkt && <> Laatst gewijzigd {tijd(inlogInfo.bijgewerkt)}{inlogInfo.door ? ` door ${inlogInfo.door}` : ''}.</>}</p>
          <label>E-mail / gebruikersnaam
            <input type="text" autoComplete="off" value={inlog.email} onChange={(e) => setInlog({ ...inlog, email: e.target.value })} required />
          </label>
          <label>Wachtwoord
            <input type="password" autoComplete="new-password" value={inlog.wachtwoord} placeholder={inlogInfo?.email ? 'Leeg laten = niet wijzigen' : ''}
              onChange={(e) => setInlog({ ...inlog, wachtwoord: e.target.value })} required={!inlogInfo?.email} />
          </label>
          <div className="du-paneel-acties">
            <button type="submit" className="du-btn du-btn-primary">Opslaan en testen</button>
          </div>
          <small>Na opslaan logt de server meteen in en haalt hij de laatste 3 weken op. Lukt het inloggen niet, dan zie je dat bovenaan.</small>
        </form>
      )}

      {paneel === 'weken' && (
        <div className="du-paneel">
          <button type="button" className="du-sluit" onClick={() => setPaneel(null)} aria-label="Sluiten"><X size={16} /></button>
          <h4>Weken in FlexDamen · {profielNaam}</h4>
          <p>Damen toont de urenstaten van de laatste 3 maanden. Vink de weken aan die je wilt ophalen; eerder opgehaalde weken worden bijgewerkt.
            {st?.beschikbaar && <> Opgezocht {tijd(st.beschikbaar.op)}.</>}</p>
          {st?.bezig && st.bezigProfiel === profiel && st.taak === 'overzicht' ? <p className="du-laden"><RefreshCw size={15} className="du-spin" /> Weken opzoeken…</p>
            : !beschikbaar.length ? <p className="du-laden">Nog geen overzicht. <button type="button" className="du-link" onClick={() => start('/api/damen-uren/overzicht')}>Opzoeken</button></p>
            : (
              <ul className="du-weken">
                {beschikbaar.map((w) => {
                  const k = `${w.jaar}-${w.week}`;
                  const hier = lokaal[k];
                  return (
                    <li key={k}>
                      <label>
                        <input type="checkbox" checked={gekozen.has(k)} onChange={() => wissel(k)} />
                        <strong>Week {w.week}</strong> <span>{w.jaar}</span>
                        <em>{w.aantal} urenstaten · {uur(w.uren)} uur</em>
                        <small className={hier ? 'is-ok' : ''}>{hier ? `opgehaald (${hier.urenstaten})` : 'nog niet opgehaald'}</small>
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}
          <div className="du-paneel-acties">
            <button type="button" className="du-btn" onClick={() => start('/api/damen-uren/overzicht')} disabled={st?.bezig}>Opnieuw opzoeken</button>
            <button type="button" className="du-btn du-btn-primary" onClick={haalGekozen} disabled={!gekozen.size || st?.bezig}>
              {gekozen.size ? `${gekozen.size} ${gekozen.size === 1 ? 'week' : 'weken'} ophalen` : 'Kies weken'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
